import {
  authentication,
  getWrapperWithLogContext,
  isProxyUser,
  isSTPUser,
  isTestUser,
} from '@ecosystem/newron-client-lib';
import { User } from '@ecosystem/newron-client-lib/build/authentication/types';
import { jwtDecode } from 'jwt-decode';

import { TEXT_BLOCK } from './constants';
import {
  Client,
  CollectionInsight,
  CollectionTag,
  CollectionTagKeys,
  CollectionTagsRecord,
  Proposition,
  UserInfo,
} from './types';

export function buildDisplayName(userInfo?: Omit<UserInfo, 'userId'>): string {
  return userInfo?.familyName && userInfo?.givenName
    ? `${userInfo?.familyName}, ${userInfo?.givenName}`
    : userInfo?.email || 'unknown user';
}

export const getLongLivedToken = async (tokenExpirationThreshold: number): Promise<string> => {
  let token = await authentication.getAccessToken();
  const decodedToken = jwtDecode(token);
  const expiredAt = decodedToken.exp * 1000;

  if (expiredAt - Date.now() < tokenExpirationThreshold) {
    token = await authentication.getAccessToken(true);
  }

  return token;
};

export const isTextBlock = (insight: Pick<CollectionInsight<unknown>, 'proposition' | 'type'>): boolean =>
  insight?.proposition === Proposition.Platform && insight?.type === TEXT_BLOCK;

/**
 * Builds client name to display in the UI. If there is more than one client
 * it will display the number of clients, else it will display the client name.
 * @param assignedClients List of assigned clients
 * @returns Client name to display in the UI
 */
export const getClientsDisplayName = (assignedClients: Client[]): string =>
  assignedClients.length > 1 ? `${assignedClients.length} clients` : assignedClients[0].clientName;

/**
 * Converts an array of CollectionTag to a CollectionTagsRecord, keyed by valid CollectionTagKeys.
 *
 * @param tags - An array of `CollectionTag` objects to be mapped. Defaults to an empty array.
 * @returns A `CollectionTagsRecord` where each key is a `CollectionTagKeys` and the value is an array of tag values.
 */
export const mapToCollectionTagKeys = (tags: CollectionTag[] = []): CollectionTagsRecord =>
  tags.reduce(
    (acc, tag) => {
      if (Object.values(CollectionTagKeys).includes(tag.key)) {
        acc[tag.key] = tag.values;
      }
      return acc;
    },
    {
      [CollectionTagKeys.Countries]: [],
      [CollectionTagKeys.ProductGroups]: [],
      [CollectionTagKeys.Periodicity]: [],
    }
  );

/**
 * Converts a record of collection tags into an array of `CollectionTag` objects.
 *
 * @param tagRecord - An object where each key is a `CollectionTagKeys` and the value is an array of strings representing tag values.
 * @returns An array of `CollectionTag` objects, each containing a `key` and its associated `values`.
 */
export const mapToCollectionTags = (tagRecord: CollectionTagsRecord): CollectionTag[] =>
  Object.entries(tagRecord ?? {}).map(([key, values]: [CollectionTagKeys, string[]]) => ({
    key,
    values,
  }));

export const logWrapper = getWrapperWithLogContext('client-collections');

export const getUserType = (user: User): string => {
  if (user?.isInternalUser) {
    return 'Internal';
  }
  if (isProxyUser(user)) {
    return 'Proxy';
  }
  if (isSTPUser(user?.userId ?? '')) {
    return 'STP';
  }
  if (isTestUser(user?.country)) {
    return 'Test';
  }

  return 'Client';
};
